// @ts-nocheck
import { DepthSelectOption, SelectOption } from "../types/misc";
import { HgNaics } from "../../types/taxonomy";
import { isEmpty } from "../../utils/jsUtils";

export const isNilOrEmpty = (value: any) =>
  value == null || value.length === 0 || Object.keys(value).length === 0;

const isNotNil = (value: any) => value !== null && value !== undefined;
const isNil = (value: any) => value === null || value === undefined;
const isNotNilOrEmpty = (value: any) => isNotNil(value) && !isEmpty(value);

export function transformIntoDepthSelectOptionTree(
  objectList: any[],
  objectToDepthSelectOptionFunction: (
    object: any,
    childOptions: DepthSelectOption[],
    depth: number,
    parentIds: string[]
  ) => DepthSelectOption,
  idKey = "id",
  parentIdKey = "parentId"
): DepthSelectOption[] {
  const groupedObjects: Map<any, Array<any>> = new Map();
  for (const object of objectList) {
    const parentId = object[parentIdKey];
    parentId instanceof Array
      ? (parentId as []).forEach((id) =>
          putIfAbsent(groupedObjects, id, object)
        )
      : putIfAbsent(groupedObjects, parentId, object);
  }

  const recursiveTransform = (
    depth: number,
    parentIds: string[],
    object: any
  ) => {
    const objectId = object[idKey];
    const transformedChildren = (groupedObjects.get(objectId) || []).map(
      (obj) => recursiveTransform(depth + 1, [...parentIds, objectId], obj)
    );

    return objectToDepthSelectOptionFunction(
      object,
      transformedChildren.sort((a, b) => a.label.localeCompare(b.label)),
      depth,
      parentIds
    );
  };

  const rootObject = groupedObjects.get("0");
  return (rootObject || [])
    .map((obj) => recursiveTransform(0, [], obj))
    .sort((a, b) => a.label.localeCompare(b.label))
    .map(rollUpOptionValueRecursively);
}

function putIfAbsent(map: Map<any, Array<any>>, key: any, value: any) {
  if (!map.has(key)) {
    map.set(key, []);
  }
  map.get(key).push(value);
}

function rollUpOptionValueRecursively(option: DepthSelectOption) {
  if (isEmpty(option.options)) {
    return {
      ...option,
      value: [option.value],
    };
  }
  return {
    ...option,
    options: option.options.map(rollUpOptionValueRecursively),
    value: calculateRolledUpValue(option),
  };
}

function calculateRolledUpValue(option: DepthSelectOption) {
  if (isEmpty(option.options)) {
    return [option.value];
  }
  const childValues = option.options.map(calculateRolledUpValue);
  return [option.value, ...childValues.flat()];
}

function naicsToSelectOption(naics: HgNaics): SelectOption {
  return {
    label: `${naics.code}: ${naics.name}`,
    value: naics.id,
  };
}

export function naicsToDepthSelectOption(
  naics: HgNaics,
  childOptions: DepthSelectOption[],
  depth: number,
  parentIds: string[]
): DepthSelectOption {
  return {
    ...naicsToSelectOption(naics),
    options: isEmpty(childOptions) ? undefined : childOptions,
    depth: depth,
    parents: parentIds,
  };
}

export function groupDepthTreeIds(ids: string[], tree: DepthSelectOption[]) {
  let idMap = new Map<string, string[]>();

  for (const id of ids) {
    // Skip if we already created a group for this id
    if (idMap.has(id)) {
      continue;
    }

    // Find the matching DepthSelectOption for this id
    const matchingOption: DepthSelectOption = getDepthSelectOption(id, tree);
    if (isNotNil(matchingOption)) {
      const parentIds: string[] = matchingOption.parents;
      const isRoot: boolean = isEmpty(parentIds);

      // if it's a root, then it must have it's own group
      if (isRoot) {
        idMap.set(id, [id]);
        continue;
      }

      // Might not be a root, but check to see if the parent is in our list
      const parentIdInList: string = parentIds.find((id) => idMap.has(id));

      // Not in the list, so it must be a root for our case
      if (isNil(parentIdInList)) {
        idMap.set(id, [id]);
        continue;
      } else {
        // The parent exists in our list. Let's check to see if we have a map entry for it already
        const parentIdWithEntry: string = parentIds.find((id) => idMap.has(id));
        if (isNotNil(parentIdWithEntry)) {
          const parentList = idMap.get(parentIdWithEntry);

          // Only add ids that haven't been added already
          if (parentList?.indexOf(id) === -1) {
            parentList?.push(id);
          }
          idMap.set(parentIdWithEntry, parentList);
          continue;
        } else {
          // So the parent exists in our list, but we don't have an entry for it yet.
          // Let's create it with both the parent and current id
          idMap.set(parentIdInList, [parentIdInList, id]);
          continue;
        }
      }
    }
  }

  return Array.from(idMap.values());
}

function getDepthSelectOption(
  id: string,
  depthSelectTree: DepthSelectOption[]
): DepthSelectOption {
  for (const option of depthSelectTree) {
    if (option.value[0] === id) {
      return option;
    } else if (option.value.includes(id) && isNotNilOrEmpty(option.options)) {
      return getDepthSelectOption(id, option.options);
    }
  }

  // Couldn't find it...
  return undefined;
}
